import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { InboundItem } from "../../types/item";
import { RootState } from "../store";
import {
  saveInboundItem,
  deleteInboundItem as deleteInboundItemFromDB,
  updateInboundItemFields,
  getInboundItemById,
  getInboundItemsFromDB,
} from "../../db/schemas/inboundItems";
import { isReallyOnline } from "../../lib/isOnline";
import { isOfflineMode } from "../../shared/offlineManager";
import {
  fetchItems,
  createItem,
  editItem,
  deleteItem,
  getHouseLocationsAPI,
  getEventsAPI
} from "../services/InboundItemsAPI";
import { MediaItem } from "../../types/item";
import { deleteImage, getAllImages } from "../../db";
import { postMessage } from "../../lib/post-message";
import { ItemStatus } from "../../types/enums/item";
import { LocationItem, EventOption } from "../../types/item";
import { DropdownOption } from "../../components/template/Drowpdown/dropdown";

interface InboundItemsState {
  items: InboundItem[]; // Server items only
  isLoading: boolean;
  error: string | null;
  houseLocations: LocationItem[];
  events: EventOption[];
}

const initialState: InboundItemsState = {
  items: [],
  isLoading: false,
  error: null,
  houseLocations: [],
  events: [],
};

export const getEvents = createAsyncThunk("inboundItems/getEvents",
  async (_, { getState }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;
      const response = await getEventsAPI(token || "");
      return response.data.map((event: any) => ({
        value: event.id.toString(),
        label: event.value,
        isRunOrderLocked: event.isRunOrderLocked,
        eventStatus_ID: event.EventStatus_ID,
      }));
    } catch (error) {
      console.error("Error fetching events:", error);
      return [] as DropdownOption[];
    }
  }
);

/**
 * Fetches items from server only
 * - Returns: Server items (source of truth)
 * - Component combines with IndexedDB pending items if needed
 */
export const getInboundItems = createAsyncThunk(
  "inboundItems/getInboundItems",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;
      const currentItems = state?.inboundItems.items || [];

      // Check if we're online and have a token
      const isOnline = await isReallyOnline();
      const isManualOffline = isOfflineMode();

      let serverItems: InboundItem[] = [];

      if (isOnline && !isManualOffline && token) {
        try {
          // Try to fetch from API
          const apiResponse = await fetchItems(token, { isPaginated: false });
          serverItems = apiResponse.data || [];

          // Helper to strip query parameters from URLs
          const stripQueryParams = (url: string): string => {
            if (!url) return url;
            return url.split("?")[0];
          };

          // Normalize server items only for fresh API responses
          serverItems = serverItems.map((item) => ({
            ...item,
            status_ID: ItemStatus.Synced,
            // Remove mediaFiles to avoid duplication - use existing media array
            mediaFiles: undefined,
            media:
              item.mediaFiles?.map((media: any) => ({
                id: media.id,
                type_ID: media.type_ID,
                type: media.type_ID === 2 ? "video" : "image",
                url: stripQueryParams(media.fileUrl || ""),
                serverUrl: media.fileUrl || "", // need for delete media from server
                thumbnailUrl: stripQueryParams(media.urL_ThumbNail),
                uploaded: true,
                attachedToItem: true,
                toDelete: false,
              })) || [],
          }));
        } catch (apiError) {
          console.warn("Failed to fetch from server, using cached:", apiError);
          serverItems = currentItems;
        }
      } else {
        // Offline - use cached items
        serverItems = currentItems;
      }

      const normalizedItems = serverItems;

      // ===== CLEANUP MOVED TO SERVICE WORKER =====
      // Trigger cleanup in service worker (non-blocking)
      postMessage({
        type: "CLEANUP_INBOUND_MEDIA",
        payload: { serverItems: normalizedItems },
      });

      return normalizedItems;
    } catch (error) {
      console.error("Error fetching inbound items:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch items"
      );
    }
  }
);

export const deleteInboundItem = createAsyncThunk(
  "inboundItems/deleteInboundItem",
  async ({ item }: { item: InboundItem }, { getState, rejectWithValue }) => {
    try {
      // ✅ Remove any pending create/update requests from the queue
      const itemId = item.id && Number(item.id) > 0 ? item.id as number : -1;
      if (item.tempId) {
        await removePendingRequestsForItem(item.tempId as number, itemId);
      }

      // Delete from IndexedDB
      if (item.tempId) {
        await deleteInboundItemFromDB(item.tempId as number);
      }
      // ✅ Only delete media blobs that haven't been uploaded yet
      const mediaBlobsToDelete = item.media?.filter((media) => !media.uploaded);
      await deleteMediaFromDB(mediaBlobsToDelete);
      const state = getState() as RootState;
      const token = state.auth.token;
      const isOnline = await isReallyOnline();
      const isManualOffline = isOfflineMode();
      const isItemServerSide = item.id && Number(item.id) > 0;
      if (isOnline && !isManualOffline && token && isItemServerSide) {
        try {
          await deleteItem(token as string, {
            itemId: item.id as number,
          });
        } catch (error) {
          postMessage({
            type: "INBOUND_ITEM_DELETION_FAILED",
            payload: {
              itemId: item.id as number,
              tempId: item.tempId as number,
              token: token,
            },
          });
        }
      } else if (isItemServerSide) {
        postMessage({
          type: "INBOUND_ITEM_DELETION_FAILED",
          payload: {
            itemId: item.id as number,
            tempId: item.tempId as number,
            token: token,
          },
        });
      }
      return { itemId: item.id, tempId: item.tempId };
    } catch (error) {
      console.error("Error deleting inbound item:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to delete item"
      );
    }
  }
);

export const createInboundItem = createAsyncThunk(
  "inboundItems/createInboundItem",
  async (itemData: InboundItem, { getState, rejectWithValue }) => {
    try {
      // ✅ Check existing item BEFORE saving
      const existingItem = await getInboundItemById(itemData.tempId as number);
      
      // ✅ If item exists and is Pending, it already has a pending request
      if (existingItem && existingItem.status_ID === ItemStatus.Pending) {
        console.log(
          `⏭️ Item with tempId ${itemData.tempId} already has pending request (status: Pending) - updating IndexedDB with latest data`
        );
        // Save the updated data to IndexedDB (to keep it in sync) and return
        await saveInboundItem(itemData);
        return await getInboundItemById(itemData.tempId as number);
      }

      const state = getState() as RootState;
      const token = state.auth.token;
      const tenant = state.auth.tenant;
      const user_ID = state.auth.userId;

      // Check if we're online and have a token
      const isOnline = await isReallyOnline();
      const isManualOffline = isOfflineMode();

      // Always save to IndexedDB first (offline-first approach)
      await saveInboundItem(itemData);
      if (!isManualOffline && isOnline && token) {
        try {
          // Try to create item via API FIRST (before uploading media)
          const apiResponse = await createItem(token, itemData);

          if (apiResponse?.status === 200) {
            const serverItem = apiResponse.data;
            const itemId = serverItem.itemId;

            // Update with server ID but keep as Pending until ALL media is done
            await updateInboundItemFields(itemData.tempId as number, {
              id: itemId,
              status_ID: ItemStatus.Pending, // ✅ Keep as Pending until media completes
            });

            // ✅ Queue ONE request to process ALL media for this item (non-blocking)
            const mediaToUpload =
              itemData?.media?.filter((media: MediaItem) => !media.uploaded && !media.toDelete) ||
              [];

            if (mediaToUpload && mediaToUpload.length > 0) {
              console.log(
                `📤 Queueing batch upload of ${mediaToUpload.length} media files for item ${itemId}`
              );

              // Queue single request for all media in this item
              await postMessage({
                type: "QUEUE_INBOUND_ITEM_MEDIA_BATCH",
                payload: {
                  itemId: itemId,
                  tempId: itemData.tempId,
                  token: token,
                  user_ID: user_ID,
                },
              });

              console.log(
                `✅ Item ${itemId} created - media batch queued to background`
              );
            } else {
              // ✅ No media to upload, mark as Synced immediately
              await updateInboundItemFields(itemData.tempId as number, {
                status_ID: ItemStatus.Synced,
              });
              console.log(`✅ Item ${itemId} fully synced (no media)`);
            }
          } else {
            console.log("Item creation failed with non-200 response");
            await updateInboundItemFields(itemData.tempId as number, {
              status_ID: ItemStatus.ErrorProcessing,
            });
          }

          const item = await getInboundItemById(itemData.tempId as number);
          return item;
        } catch (apiError) {
          await updateInboundItemFields(itemData.tempId as number, {
            status_ID: ItemStatus.ErrorProcessing,
          });

          // Queue the request for retry
          // Note: No need to check for pending requests here - already checked at the beginning
          try {
            await postMessage({
              type: "INBOUND_ITEM_CREATION_FAILED",
              payload: {
                itemData: itemData,
                token: token,
                tenant: tenant,
                error:
                  apiError instanceof Error
                    ? apiError.message
                    : "API call failed",
              },
            });
            console.log("Inbound item creation queued for retry");
          } catch (queueError) {
            console.error("Failed to queue inbound item creation:", queueError);
            // If postMessage fails, ensure status is ErrorProcessing (already set above, but explicit)
            await updateInboundItemFields(itemData.tempId as number, {
              status_ID: ItemStatus.ErrorProcessing,
            });
          }

          // Return the item from IndexedDB with ErrorProcessing status
          const item = await getInboundItemById(itemData.tempId as number);
          return item;
        }
      } else {
        // Queue the request for retry when offline (service worker won't catch this)
        // Note: No need to check for pending requests here - already checked at the beginning
        try {
          await postMessage({
            type: "INBOUND_ITEM_CREATION_FAILED",
            payload: {
              itemData: itemData,
              token: token,
              tenant: tenant,
              error: "Offline mode - no network connection",
            },
          });
          console.log("Inbound item creation queued for retry when online");
        } catch (queueError) {
          console.error("Failed to queue inbound item creation:", queueError);
          // If postMessage fails, set status to ErrorProcessing so re-editing can retry
          await updateInboundItemFields(itemData.tempId as number, {
            status_ID: ItemStatus.ErrorProcessing,
          });
        }

        // Return the item from IndexedDB (status may be Pending or ErrorProcessing)
        const item = await getInboundItemById(itemData.tempId as number);
        return item;
      }
    } catch (error) {
      console.error("Error creating inbound item:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to create item"
      );
    }
  }
);

/**
 * Helper function to update item data via API
 */
async function updateItemData(
  updatedItemData: InboundItem,
  token: string,
  hasItemChanges: boolean
): Promise<void> {
  if (!hasItemChanges) {
    console.log(`⏭️ Skipping editItem API call - no item data changes`);
    return;
  }

  console.log(`📝 Item data changed - calling editItem API`);
  const apiResponse = await editItem(token, updatedItemData);

  if (apiResponse?.status !== 200) {
    console.log("Item update failed with non-200 response");
    await updateInboundItemFields(updatedItemData.tempId as number, {
      status_ID: ItemStatus.ErrorProcessing,
    });
    throw new Error("Item update failed");
  }
  updateInboundItemFields(updatedItemData.tempId as number, {
    status_ID: ItemStatus.Synced,
  });
  console.log(`✅ Item data updated successfully`);
}

export const updateInboundItem = createAsyncThunk(
  "inboundItems/updateInboundItem",
  async (
    {
      updatedItemData,
      hasItemChanges = true,
    }: { updatedItemData: InboundItem; hasItemChanges?: boolean },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      // ✅ Check existing item BEFORE saving
      const existingItem = await getInboundItemById(updatedItemData.tempId as number);

      // ✅ If item exists and is Pending, it already has a pending request
      if (existingItem && existingItem.status_ID === ItemStatus.Pending) {
        console.log(
          `⏭️ Item with tempId ${updatedItemData.tempId} already has pending request (status: Pending) - updating IndexedDB with latest data`
        );
        // Save the updated data to IndexedDB (to keep it in sync) and return
        await saveInboundItem(updatedItemData);
        return await getInboundItemById(updatedItemData.tempId as number);
      }

      await saveInboundItem(updatedItemData);

      const state = getState() as RootState;
      const token = state.auth.token;
      const tenant = state.auth.tenant;
      const user_ID = state.auth.userId;

      // Check if item only has tempId (no real ID) - treat as create instead of update
      const hasInvalidId =
        !updatedItemData.id || Number(updatedItemData.id) <= 0;

      // If no pending request exists and item only has tempId, enter create process
      if (hasInvalidId) {
        console.log(
          "🔄 Item has no real ID (only tempId) - treating as create instead of update"
        );

        // Reset status to Pending for creation attempt (keep existing tempId)
        const itemToCreate = {
          ...updatedItemData,
          status_ID: ItemStatus.Pending,
          id: undefined,
        };

        // Call createInboundItem instead
        const result = await createInboundItem(itemToCreate)(
          dispatch,
          getState,
          undefined
        );
        return result.payload;
      }

      // Check if we're online and have a token
      const isOnline = await isReallyOnline();
      const isManualOffline = isOfflineMode();

      if (!isManualOffline && isOnline && token) {
        try {
          const itemId = updatedItemData.id as number;

          // 1. Update item data if needed
          if (hasItemChanges) {
            await updateItemData(updatedItemData, token, hasItemChanges);
          }

          // 2. Handle media deletions if any
          const mediaToDelete =
            updatedItemData.media?.filter((media) => media.toDelete) || [];
          if (mediaToDelete.length > 0) {
            console.log(
              `🗑️ Queueing ${mediaToDelete.length} media deletions for item ${itemId}`
            );
            // Offload to service worker
            await postMessage({
              type: "QUEUE_INBOUND_ITEM_MEDIA_DELETE",
              payload: {
                itemId: itemId,
                tempId: updatedItemData.tempId,
                token: token,
              },
            });
          }
          // 3. Handle media uploads if any
          const mediaToUpload =
            updatedItemData?.media?.filter(
              (media: MediaItem) => !media.uploaded && !media.toDelete
            ) || [];

          if (mediaToUpload.length > 0) {
            
            // Offload to service worker
            await postMessage({
              type: "QUEUE_INBOUND_ITEM_MEDIA_BATCH",
              payload: {
                itemId: itemId,
                tempId: updatedItemData.tempId,
                token: token,
                user_ID: user_ID,
              },
            });
          }

          return await getInboundItemById(updatedItemData.tempId as number);
        } catch (apiError) {
          // Set status to ErrorProcessing on API failure
          await updateInboundItemFields(updatedItemData.tempId as number, {
            status_ID: ItemStatus.ErrorProcessing,
          });

          // Queue the request for retry
          // Note: No need to check for pending requests here - already checked at the beginning
          try {
            await postMessage({
              type: "INBOUND_ITEM_UPDATE_FAILED",
              payload: {
                itemData: updatedItemData,
                token: token,
                tenant: tenant,
                error:
                  apiError instanceof Error
                    ? apiError.message
                    : "API call failed",
              },
            });
          } catch (queueError) {
            console.error("Failed to queue inbound item update:", queueError);
            // If postMessage fails, ensure status is ErrorProcessing (already set above, but explicit)
            await updateInboundItemFields(updatedItemData.tempId as number, {
              status_ID: ItemStatus.ErrorProcessing,
            });
          }

          // Return the item from IndexedDB with ErrorProcessing status
          const item = await getInboundItemById(
            updatedItemData.tempId as number
          );

          return item;
        }
      } else {
        // Queue the request for retry when offline
        // Note: No need to check for pending requests here - already checked at the beginning
        try {
          await postMessage({
            type: "INBOUND_ITEM_UPDATE_FAILED",
            payload: {
              itemData: updatedItemData,
              token: token,
              tenant: tenant,
              error: "Offline mode - no network connection",
            },
          });
          console.log("Inbound item update queued for retry when online");
        } catch (queueError) {
          console.error("Failed to queue inbound item update:", queueError);
          // If postMessage fails, set status to ErrorProcessing so re-editing can retry
          await updateInboundItemFields(updatedItemData.tempId as number, {
            status_ID: ItemStatus.ErrorProcessing,
          });
        }

        // Return the item from IndexedDB (status may be Pending or ErrorProcessing)
        const item = await getInboundItemById(updatedItemData.tempId as number);
        return item;
      }
    } catch (error) {
      console.error("Error updating inbound item:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to update item"
      );
    }
  }
);

async function deleteMediaFromDB(media: MediaItem[] | undefined) {
  if (!media || media.length === 0) return;

  for (const mediaItem of media) {
    if (!mediaItem.uuid) continue;

    try {
      await deleteImage(mediaItem.uuid as string);
      // Also delete thumbnail if it exists (videos only)
      await deleteImage(`${mediaItem.uuid as string}_thumb`).catch(() => {
        // Thumbnail might not exist - silently ignore
      });
    } catch (error) {
      // Blob might have been deleted by another thread - non-fatal
      console.warn(
        `Failed to delete media blob ${mediaItem.uuid} (non-fatal):`,
        error
      );
    }
  }
}
async function removePendingRequestsForItem(
  tempId: number,
  itemId?: number
): Promise<void> {
  try {
    // Offload to service worker - one-way communication (fire and forget)
    await postMessage({
      type: "REMOVE_REQUESTS_FROM_QUEUE",
      payload: {
        tempId: tempId,
        itemId: itemId,
      },
    });
    console.log(
      `🗑️ Requested removal of pending request(s) for item (tempId: ${tempId}, itemId: ${itemId || "none"})`
    );
  } catch (error) {
    console.error("Error removing pending requests from queue:", error);
  }
}
// DEPRECATED: No longer needed with simplified architecture
// (IndexedDB cleanup now handled by service worker)

/**
 * Cleans up orphaned media blobs from IndexedDB that aren't referenced by any item
 * This catches blobs from:
 * - Abandoned create/edit sessions
 * - Failed upload attempts
 * - Any other orphaned blobs
 */
export async function cleanupOrphanedMedia(): Promise<void> {
  try {
    console.log("🧹 Starting orphaned media cleanup...");

    // Get all items from IndexedDB
    const allItems = (await getInboundItemsFromDB()) || [];

    // Collect UUIDs that should be KEPT (not uploaded/attached yet)
    const keepUUIDs = new Set<string>();

    // Collect UUIDs that are SAFE TO DELETE (uploaded AND attached)
    const safeToDeleteUUIDs = new Set<string>();

    allItems.forEach((item) => {
      if (item.media) {
        item.media.forEach((media) => {
          if (media.uuid) {
            const isUploadedAndAttached =
              media.uploaded && media.attachedToItem;

            if (isUploadedAndAttached) {
              // Safe to delete - already on server
              safeToDeleteUUIDs.add(media.uuid);
              // Also safe to delete video thumbnail
              if (media.type === "video") {
                safeToDeleteUUIDs.add(`${media.uuid}_thumb`);
              }
            } else {
              // Keep - still needed locally
              keepUUIDs.add(media.uuid);
              // Also keep video thumbnail if not uploaded yet
              if (media.type === "video") {
                keepUUIDs.add(`${media.uuid}_thumb`);
              }
            }
          }
        });
      }
    });

    console.log(
      `📋 Found ${keepUUIDs.size} media to keep, ${safeToDeleteUUIDs.size} safe to delete in ${allItems.length} items`
    );

    // Get all media blobs from IndexedDB
    const allBlobs = await getAllImages();

    if (!allBlobs || allBlobs.length === 0) {
      console.log("✅ No media blobs in IndexedDB");
      return;
    }

    console.log(`💾 Found ${allBlobs.length} media blobs in IndexedDB`);

    // Find blobs to delete:
    // 1. Orphaned blobs (not referenced by any item)
    // 2. Blobs that are uploaded and attached (safe to delete)
    const blobsToDelete = allBlobs.filter((blobEntry) => {
      const isOrphaned =
        !keepUUIDs.has(blobEntry.id) && !safeToDeleteUUIDs.has(blobEntry.id);
      const isSafeToDelete = safeToDeleteUUIDs.has(blobEntry.id);
      return isOrphaned || isSafeToDelete;
    });

    if (blobsToDelete.length === 0) {
      console.log("✅ No media blobs to cleanup");
      return;
    }

    console.log(
      `🗑️ Found ${blobsToDelete.length} media blobs to delete (orphaned or uploaded), deleting...`
    );

    console.log(
      blobsToDelete.map((b) => ({
        id: b.id,
        reason: safeToDeleteUUIDs.has(b.id) ? "uploaded" : "orphaned",
      }))
    );

    // Delete blobs
    await Promise.all(
      blobsToDelete.map((blobEntry) => deleteImage(blobEntry.id))
    );

    console.log(
      `✅ Successfully cleaned up ${blobsToDelete.length} media blobs`
    );
  } catch (error) {
    console.error("❌ Error during orphaned media cleanup:", error);
  }
}

/**
 * Cleanup fully synced items from IndexedDB
 * - Items with status_ID === 2 (Synced)
 * - All media uploaded and attached
 * - Exists on server
 *
 * Runs in background, non-blocking
 */
export async function cleanupSyncedItems(
  serverItems: InboundItem[]
): Promise<void> {
  try {
    console.log("🧹 Checking for synced items to cleanup from IndexedDB...");

    // Get all items from IndexedDB
    const indexDBItems = (await getInboundItemsFromDB()) || [];

    if (indexDBItems.length === 0) {
      console.log("✅ No items in IndexedDB to cleanup");
      return;
    }

    // Build map of server items by ID for quick lookup
    const serverItemsMap = new Map(serverItems.map((item) => [item.id, item]));

    // Find items to cleanup
    const itemsToCleanup: number[] = [];

    for (const indexDBItem of indexDBItems) {
      const hasRealId = indexDBItem.id && Number(indexDBItem.id) > 0;

      if (hasRealId && serverItemsMap.has(indexDBItem.id)) {
        // Item exists on server
        const isSynced = indexDBItem.status_ID === 2; // ItemStatus.Synced
        const hasPendingMedia = indexDBItem.media?.some(
          (m) => !m.uploaded || !m.attachedToItem
        );

        if (isSynced && !hasPendingMedia) {
          // ✅ Fully synced - safe to delete from IndexedDB
          if (indexDBItem.tempId) {
            itemsToCleanup.push(indexDBItem.tempId as number);
            console.log(
              `🧹 Marking item ${indexDBItem.id} (tempId: ${indexDBItem.tempId}) for cleanup - fully synced`
            );
          }
        }
      }
    }

    if (itemsToCleanup.length === 0) {
      console.log("✅ No synced items to cleanup");
      return;
    }

    console.log(
      `🗑️ Cleaning up ${itemsToCleanup.length} synced items from IndexedDB`
    );

    // Delete synced items from IndexedDB (including media blobs)
    await Promise.all(
      itemsToCleanup.map(async (tempId) => {
        try {
          // Get item to access media before deleting
          const item = indexDBItems.find((i) => i.tempId === tempId);

          // Cleanup media blobs first (if uploaded, they're safe to delete)
          if (item?.media) {
            await deleteMediaFromDB(item.media);
          }

          // Then delete the item record
          await deleteInboundItemFromDB(tempId);
          console.log(
            `✅ Cleaned up item tempId: ${tempId} (including ${
              item?.media?.length || 0
            } media blobs)`
          );
        } catch (error) {
          console.warn(`Failed to cleanup item tempId: ${tempId}:`, error);
        }
      })
    );

    console.log(
      `✅ Successfully cleaned up ${itemsToCleanup.length} synced items`
    );
  } catch (error) {
    console.error("❌ Error during synced items cleanup:", error);
  }
}

export const getHouseLocations = createAsyncThunk(
  "inboundItems/getHouseLocations",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;
      const response = await getHouseLocationsAPI(token || "");
      return response.data;
    } catch (error) {
      console.error("Error fetching house locations:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch house locations"
      );
    }
  }
);

const inboundItemsSlice = createSlice({
  name: "inboundItems",
  initialState,
  reducers: {
    addItem: (state, action) => {
      const item = action.payload as InboundItem;
      state.items = [...state.items, item];
    },
    updateItem: (state, action) => {
      const { localId, serverData } = action.payload;
      const index = state.items.findIndex((item) => item.id === localId);
      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          ...serverData,
        };
      }
    },
    removeItem: (state, action) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    setItems: (state, action) => {
      state.items = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Handle getInboundItems thunk
    builder
      .addCase(getInboundItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getInboundItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload as InboundItem[];
        state.error = null;
      })
      .addCase(getInboundItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // Don't clear existing items on rejection
      })
      // Handle createInboundItem thunk
      .addCase(createInboundItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createInboundItem.fulfilled, (state) => {
        state.isLoading = false;
        // Don't add to Redux - component will fetch from IndexedDB
        state.error = null;
      })
      .addCase(createInboundItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Handle deleteInboundItem thunk
      .addCase(deleteInboundItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteInboundItem.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove from items
        state.items = state.items.filter(
          (item) => item.id !== action.payload.itemId
        );
        state.error = null;
      })
      .addCase(deleteInboundItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Handle updateInboundItem thunk
      .addCase(updateInboundItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateInboundItem.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedItem = action.payload as InboundItem;

        // Update in items if it has a real ID
        if (updatedItem.id && Number(updatedItem.id) > 0) {
          const index = state.items.findIndex(
            (item) => item.id === updatedItem.id
          );
          if (index !== -1) {
            state.items[index] = updatedItem;
          }
        }

        state.error = null;
      })
      .addCase(updateInboundItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getHouseLocations.fulfilled, (state, action) => {
        state.houseLocations = (action.payload as LocationItem[]) || [];
      })
      // Handle getEvents thunk
      .addCase(getEvents.fulfilled, (state, action) => {
        state.events = action.payload as EventOption[];
      })
  },
});

// ===== SELECTORS =====

export const selectItems = (state: RootState) => state.inboundItems.items;
export const selectIsLoading = (state: RootState) =>
  state.inboundItems.isLoading;
export const selectError = (state: RootState) => state.inboundItems.error;
export const selectHouseLocations = (state: RootState) =>
  state.inboundItems.houseLocations;

// Alias for backward compatibility
export const selectServerItems = selectItems;

export const {
  addItem,
  updateItem,
  removeItem,
  setItems,
  setLoading,
  setError,
  clearError,
} = inboundItemsSlice.actions;
export default inboundItemsSlice.reducer;
